import type { AwsRum, AwsRumConfig } from "aws-rum-web";
import { UserContext } from "@/context/UserContext";
import { debug, error as loggerError } from "@/helpers/logger";

export class LoggerClient {
  private awsRum: AwsRum | undefined;

  private initializing = true;

  private queue: Array<{ event: string; data: Record<string, unknown> }> = [];

  private readonly MAX_QUEUE_SIZE = 25;

  private prodDomains = ["dp.trustlogix.io", "dp-eu.trustlogix.io"];

  private isProdEnv = Boolean(
    this.prodDomains.includes(window.location.hostname),
  );

  private isLocalhost = Boolean(
    window.location.hostname === "localhost" ||
      // [::1] is the IPv6 localhost address.
      window.location.hostname === "[::1]" ||
      // 127.0.0.0/8 are considered localhost for IPv4.
      window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
      ),
  );

  private get config() {
    if (this.isProdEnv) {
      return {
        identityPoolId: "us-east-1:79ef110f-6266-481e-a936-4ffece3954de",
        endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
        APPLICATION_ID: "28f9a69a-03d0-42fc-9eac-ba2a81a4da88",
      };
    }
    return {
      identityPoolId: "us-east-1:b6ba06bb-7ad7-4a96-9fa2-f5a25afe0fa9",
      endpoint: "https://dataplane.rum.us-east-1.amazonaws.com",
      APPLICATION_ID: "116b80a2-7a52-4cce-9697-828264492820",
    };
  }

  private addTenantId(data: Record<string, unknown>) {
    const computeData = { ...(data || {}) };
    const user = UserContext.getUser();
    if (user) {
      computeData.userID = user.userId;
      computeData.tenantId = user.tenant;
    }
    return computeData;
  }

  async init() {
    if (this.isLocalhost) {
      this.initializing = false;
      return;
    }
    try {
      const { AwsRum } = await import("aws-rum-web");

      const config: AwsRumConfig = {
        sessionSampleRate: 0.6,
        identityPoolId: this.config.identityPoolId,
        endpoint: this.config.endpoint,
        telemetries: ["errors", "performance", "interaction"],
        allowCookies: true,
        enableXRay: false,
      };

      const APPLICATION_ID: string = this.config.APPLICATION_ID;
      const APPLICATION_VERSION: string = "1.0.0";
      const APPLICATION_REGION: string = "us-east-1";

      this.awsRum = new AwsRum(
        APPLICATION_ID,
        APPLICATION_VERSION,
        APPLICATION_REGION,
        config,
      );
      this.flushQueue();
    } catch {
      // Ignore errors thrown during CloudWatch RUM web client initialization
    } finally {
      this.initializing = false;
    }
  }

  private enqueue(event: string, data: Record<string, unknown>) {
    if (this.queue.length >= this.MAX_QUEUE_SIZE) return;
    this.queue.push({ event, data });
  }

  private flushQueue() {
    const items = this.queue.splice(0);
    items.forEach(({ event, data }) => this.awsRum?.recordEvent(event, data));
  }

  private buildErrorData(error: object | string) {
    const data = this.addTenantId({});
    data.error = this.stringifyError(error);
    return data;
  }

  private stringifyError(error: object | string): string {
    if (typeof error === "string") return error;
    if (error instanceof Error) {
      return JSON.stringify({
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
    }
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  isReady() {
    return this.awsRum !== undefined;
  }

  recordError(error: object | string) {
    if (this.isReady()) {
      this.awsRum?.recordEvent("Error", this.buildErrorData(error));
      return;
    }
    loggerError("LoggerClient", error);
    if (this.initializing) {
      this.enqueue("Error", this.buildErrorData(error));
    }
  }

  recordEvent(event: string, data: Record<string, unknown>) {
    const computeData = this.addTenantId(data);
    if (this.isReady()) {
      this.awsRum?.recordEvent(event, computeData);
      return;
    }
    debug("LoggerClient", event, computeData);
    if (this.initializing) {
      this.enqueue(event, computeData);
    }
  }
}

const loggerClient = new LoggerClient();
void loggerClient.init();

export default loggerClient;
